/**
 * Show a browser notification, on browsers that allow it and without throwing
 * on the ones that do not.
 *
 * ## Why this exists
 *
 * **Chrome on Android has never supported the `Notification` constructor.** The
 * `Notification` object is present and `Notification.permission` can be
 * `'granted'`, so the obvious guard —
 *
 *     if (typeof Notification !== 'undefined' && Notification.permission === 'granted')
 *
 * — passes, and the very next line throws:
 *
 *     TypeError: Failed to construct 'Notification': Illegal constructor.
 *                Use ServiceWorkerRegistration.showNotification() instead.
 *
 * That is `WEFAQNA-FRONTEND-8`. It surfaced as an **unhandled rejection** inside
 * a socket event handler, which is worse than a missing notification: it threw
 * before the `notificationCallbacks` loop below it, so every other consumer of
 * that event was skipped too. Android Chrome is a large share of our web
 * traffic, so this was most of it.
 *
 * ## The rule
 *
 * Go through the service worker whenever one is registered — that is the path
 * Android requires and the path every other browser also supports. Fall back to
 * the constructor only where no worker is available (a desktop browser with the
 * worker still registering, for instance).
 *
 * Never throws. A notification is a courtesy; failing to show one must not take
 * the caller down with it.
 *
 * @returns {Promise<boolean>} whether a notification was actually shown.
 */
export async function showBrowserNotification(title, options = {}) {
  try {
    if (typeof Notification === 'undefined') return false;
    if (Notification.permission !== 'granted') return false;

    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration && typeof registration.showNotification === 'function') {
        await registration.showNotification(title, options);
        return true;
      }
    }

    // No worker: the constructor is the only route, and works on desktop.
    // Still guarded, because this is exactly where Android Chrome throws.
    // eslint-disable-next-line no-new
    new Notification(title, options);
    return true;
  } catch {
    return false;
  }
}

export default showBrowserNotification;
